// Theme Context - Mode Clair/Sombre pour AbracadaBati
import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Récupérer la préférence sauvegardée ou utiliser la préférence système
    const saved = localStorage.getItem('abracadabati-theme');
    if (saved) {
      return saved === 'dark';
    }
    // Préférence système
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Sauvegarder la préférence
    localStorage.setItem('abracadabati-theme', isDarkMode ? 'dark' : 'light');
    
    // Appliquer la classe sur le document
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Composant Toggle simple (icône seule)
// ═══════════════════════════════════════════════════════════════════════════════

export function ThemeToggle({ className = '', size = 'default' }) {
  const { isDarkMode, toggleTheme } = useTheme();
  
  // Tailles disponibles
  const sizeClasses = {
    small: 'w-6 h-6',
    default: 'w-10 h-10',
  };
  const iconSizeClasses = {
    small: 'w-3 h-3',
    default: 'w-5 h-5',
  };
  
  return (
    <button
      onClick={toggleTheme}
      className={`
        relative inline-flex items-center justify-center
        ${sizeClasses[size] || sizeClasses.default} rounded-full
        bg-slate-100 dark:bg-slate-800
        hover:bg-slate-200 dark:hover:bg-slate-700
        text-slate-600 dark:text-slate-300
        transition-colors duration-200
        ${className}
      `}
      aria-label={isDarkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
      data-testid="theme-toggle"
    >
      {/* Soleil */}
      <svg
        className={`absolute ${iconSizeClasses[size] || iconSizeClasses.default} transition-all duration-300 ${
          isDarkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
        }`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <circle cx="12" cy="12" r="5" />
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
      </svg>
      
      {/* Lune */}
      <svg
        className={`absolute ${iconSizeClasses[size] || iconSizeClasses.default} transition-all duration-300 ${
          isDarkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
        }`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
      </svg>
    </button>
  );
}

// Version Mini pour le Header (fond clair)
export function ThemeToggleMini({ className = '' }) {
  const { isDarkMode, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={`
        w-9 h-9 rounded-xl flex items-center justify-center
        bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600
        border border-slate-200 dark:border-slate-600
        transition-all duration-200 hover:scale-105
        shadow-sm
        ${className}
      `}
      aria-label={isDarkMode ? 'Mode clair' : 'Mode sombre'}
      data-testid="theme-toggle-mini"
    >
      {isDarkMode ? (
        <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Composant Toggle avec label (pour les paramètres)
// ═══════════════════════════════════════════════════════════════════════════════

export function ThemeToggleWithLabel({ className = '' }) {
  const { isDarkMode, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={`
        flex items-center gap-3 w-full px-3 py-2 rounded-lg
        hover:bg-slate-100 dark:hover:bg-slate-800
        transition-colors duration-200
        ${className}
      `}
      data-testid="theme-toggle-with-label"
    >
      {/* Icône */}
      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
        {isDarkMode ? (
          <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="5" />
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
          </svg>
        )}
      </div>
      
      {/* Label */}
      <div className="flex-1 text-left">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {isDarkMode ? 'Mode sombre' : 'Mode clair'}
        </span>
      </div>
      
      {/* Switch */}
      <div className={`
        relative w-11 h-6 rounded-full transition-colors duration-200
        ${isDarkMode ? 'bg-violet-500' : 'bg-slate-300'}
      `}>
        <div className={`
          absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200
          ${isDarkMode ? 'translate-x-6' : 'translate-x-1'}
        `} />
      </div>
    </button>
  );
}
